import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { IconContext } from 'react-icons';
import { MdGamepad } from 'react-icons/md';
import { SiTheconversation } from 'react-icons/si';
import { FaPeopleGroup } from 'react-icons/fa6';
import { IoLibrary } from 'react-icons/io5';
import { GrGallery } from 'react-icons/gr';
import { FaMicroscope } from 'react-icons/fa';
import { HiMenu, HiX } from 'react-icons/hi';

// Hoist static navItems outside component to prevent recreation on every render
const NAV_ITEMS = [
    { to: '/', icon: <MdGamepad />, text: 'Home', end: true },
    { to: '/about-project', icon: <SiTheconversation />, text: 'About Project' },
    { to: '/team', icon: <FaPeopleGroup />, text: 'Team' },
    { to: '/papers', icon: <IoLibrary />, text: 'Papers' },
    { to: '/reports-policies', icon: <FaMicroscope />, text: 'Reports & Policies' },
    { to: '/gallery&media', icon: <GrGallery />, text: 'Gallery & Media' },
] as const;

const MobileHeader: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const drawerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const scrollbarWidthRef = useRef<number>(0);

    const toggleDrawer = () => setIsOpen(prev => !prev);
    const closeDrawer = useCallback(() => setIsOpen(false), []);

    // Prevent body scroll when drawer is open (without layout shift)
    useEffect(() => {
        if (isOpen) {
            // Cache scrollbar width calculation
            if (scrollbarWidthRef.current === 0) {
                scrollbarWidthRef.current = window.innerWidth - document.documentElement.clientWidth;
            }
            const scrollbarWidth = scrollbarWidthRef.current;

            document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
            document.body.classList.add('overflow-hidden');
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }
        } else {
            document.body.classList.remove('overflow-hidden');
            document.body.style.paddingRight = '';
            document.documentElement.style.removeProperty('--scrollbar-width');
        }
        return () => {
            document.body.classList.remove('overflow-hidden');
            document.body.style.paddingRight = '';
            document.documentElement.style.removeProperty('--scrollbar-width');
        };
    }, [isOpen]);

    // Combined keyboard event handler (Esc + Focus trap)
    useEffect(() => {
        if (!isOpen) return;

        const focusableElements = drawerRef.current?.querySelectorAll(
            'a[href], button:not([disabled])'
        );
        const firstElement = focusableElements?.[0] as HTMLElement | undefined;
        const lastElement = focusableElements?.[focusableElements.length - 1] as HTMLElement | undefined;

        const handleKeyboard = (e: KeyboardEvent) => {
            // Handle Esc key
            if (e.key === 'Escape') {
                closeDrawer();
                buttonRef.current?.focus();
                return;
            }

            // Handle Tab for focus trap
            if (e.key === 'Tab' && firstElement && lastElement) {
                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyboard);
        firstElement?.focus();

        return () => document.removeEventListener('keydown', handleKeyboard);
    }, [isOpen, closeDrawer]);

    return (
        <div className="md:hidden">
            {/* Hamburger Button */}
            <button
                ref={buttonRef}
                onClick={toggleDrawer}
                className="fixed top-4 left-4 z-70 w-14 h-14 flex items-center justify-center bg-gray-800 text-ghColorPrimary-500 rounded-full hover:bg-gray-700 transition-colors shadow-lg"
                aria-label="Open navigation menu"
                aria-expanded={isOpen}
            >
                <HiMenu className="w-7 h-7" />
            </button>

            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-md z-55"
                    onClick={closeDrawer}
                    aria-hidden="true"
                />
            )}

            {/* Drawer */}
            <div
                ref={drawerRef}
                className={`fixed top-0 left-0 h-full w-64 bg-gray-900 shadow-xl z-60 transform transition-transform duration-300 ease-in-out ${
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
                aria-label="Mobile navigation"
                role="navigation"
            >
                <IconContext value={{ className: 'react-icons' }}>
                    {/* Close button inside drawer */}
                    <button
                        onClick={closeDrawer}
                        className="absolute top-4 right-4 p-2 text-ghColorPrimary-500 hover:bg-gray-800 rounded-full transition-colors"
                        aria-label="Close navigation menu"
                    >
                        <HiX className="w-6 h-6" />
                    </button>

                    <div className="flex flex-col h-full pt-20 px-4 space-y-2">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                onClick={closeDrawer}
                                className={({ isActive }) =>
                                    `flex items-center gap-4 p-3 rounded-lg text-white transition-colors ${
                                        isActive
                                            ? 'bg-ghColorPrimary-600 shadow-lg'
                                            : 'hover:bg-gray-800'
                                    }`
                                }
                            >
                                {() => (
                                    <>
                                        <span className="text-2xl">{item.icon}</span>
                                        <span className="font-medium">{item.text}</span>
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </div>
                </IconContext>
            </div>
        </div>
    );
};

export default MobileHeader;
